import { cloneDeep, uniqBy } from 'lodash-es';
import { makeObservable, observable, reaction, runInAction } from 'mobx';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { SystemRoles } from '~constants/enums';
import { Access } from '~hooks/useAccess/models';
import { Company, CompanyBasic } from '~hooks/useCompany/models';
import { CompanySettings } from '~hooks/useCompanySettings/models';
import { Permission } from '~hooks/usePermissions/models';
import { RoleItem, User } from '~hooks/useUsers/models';
import { Pagination } from '~services/pagination';
import { RootStore } from '~store/RootStore';

const DEFAULT_PAGINATION_STATE: Pagination = { limit: MAX_API_PAGE_SIZE, page: 1 };

class UserStore {
  user: User = {} as User;
  userRoles: RoleItem[] = [];
  userCompany: Company = {} as Company;
  // Empty until hydrated at bootstrap; every setting reads as `false` (fail-closed).
  companySettings: CompanySettings = new CompanySettings({});
  userCompanies: CompanyBasic[] = [];
  userCompaniesPagination: Pagination = DEFAULT_PAGINATION_STATE;
  currentCompanies: CompanyBasic[] = [];
  hasSetInitialCompany = false;
  isCurrentCompanyActive = false;
  isViewOnlyUser = false;
  isDriver = false;
  isForeman = false;
  userPermissions: Permission[] = [];
  userAccess: Access[] = [];
  constructor(readonly rootStore: RootStore) {
    makeObservable(this, {
      user: observable,
      userRoles: observable,
      userCompany: observable,
      companySettings: observable,
      userCompanies: observable,
      currentCompanies: observable,
      userPermissions: observable,
      userAccess: observable,
      isCurrentCompanyActive: observable,
    });
    this.rootStore = rootStore;

    reaction(
      () => this.userCompanies,
      (companies) => {
        if (companies.length > 0 && !this.hasSetInitialCompany) {
          runInAction(() => {
            this.currentCompanies = [companies[0]]; // The user's company should be the default current company
            this.hasSetInitialCompany = true;
          });
        }
      },
    );

    reaction(
      () => [this.userCompany, this.currentCompanies],
      () => {
        runInAction(() => {
          this.isCurrentCompanyActive =
            this.userCompany.id === this.currentCompanies[0]?.id;
        });
      },
    );

    reaction(
      () => this.user?.userRoles,
      (roles) => {
        runInAction(() => {
          this.isViewOnlyUser =
            roles?.every((role: RoleItem) => role.name === SystemRoles.VIEWER) || false;
          this.isDriver =
            roles?.every((role: RoleItem) => role.name === SystemRoles.DRIVER) || false;
          this.isForeman =
            roles?.every((role: RoleItem) => role.name === SystemRoles.FOREMAN) || false;
        });
      },
    );
  }

  clearUser() {
    runInAction(() => {
      this.user = {} as User;
    });
  }

  setUserCompany(company: Company) {
    runInAction(() => {
      this.userCompany = company;
    });
  }
  updateUserCompany(company: Company) {
    runInAction(() => {
      this.setUserCompany(company);
    });
  }
  setCompanySettings(companySettings: CompanySettings) {
    runInAction(() => {
      this.companySettings = companySettings;
    });
  }
  setUserCompanies(companies: CompanyBasic[], pagination?: Pagination) {
    runInAction(() => {
      this.userCompanies = uniqBy(
        [...this.userCompanies, ...companies],
        (company) => company.id,
      );
      this.userCompaniesPagination = {
        limit: DEFAULT_PAGINATION_STATE.limit,
        after: pagination?.after ?? '',
        page: DEFAULT_PAGINATION_STATE.page,
      };
    });
  }
  clearUserCompanies() {
    runInAction(() => {
      this.userCompanies = [];
      this.userCompaniesPagination = DEFAULT_PAGINATION_STATE;
      this.hasSetInitialCompany = false;
    });
  }
  setCurrentCompanies(companies: CompanyBasic[]) {
    runInAction(() => {
      this.currentCompanies = [...companies];
    });
  }
  // Public get userInitials(): string {
  //   Return `${this.user?.firstName?.charAt(0)}${this.user?.lastName?.charAt(0)}` || '';
  // }
  setUserRoles(userRoles: RoleItem[]) {
    runInAction(() => {
      this.userRoles = [...userRoles];
    });
  }

  setUser(user: User) {
    runInAction(() => {
      this.user = cloneDeep(user);
    });
  }

  updateUser(user: User) {
    runInAction(() => {
      // Update if it's current user
      if (user.id === this.user.id) {
        this.setUser(user);
      }
    });
  }

  setPermissions(permissions: Permission[]) {
    runInAction(() => {
      this.userPermissions = [...permissions];
    });
  }
  getPermissions() {
    // make userPermissions a Set to make it faster and easier to check if a user has a permission
    const userPermissionsSet = new Set(this.userPermissions.map((perm) => perm.name));
    return {
      // User Permissions
      canEditUser: userPermissionsSet.has('edit_user'),
      canDeleteUser: userPermissionsSet.has('delete_user'),
      canCreateUser: userPermissionsSet.has('create_user'),
      canEditUserHideRate: userPermissionsSet.has('edit_user_hide_rate'),

      // Project Permissions
      canEditProject: userPermissionsSet.has('edit_project'),
      canDeleteProject: userPermissionsSet.has('delete_project'),
      canCreateProject: userPermissionsSet.has('create_project'),

      // Order Permissions
      canEditOrder: userPermissionsSet.has('edit_order'),
      canDeleteOrder: userPermissionsSet.has('delete_order'),
      canCreateOrder: userPermissionsSet.has('create_order'),
      canCreateOrderRequest: userPermissionsSet.has('create_order_request'),
      canEmailLoadDigest: userPermissionsSet.has('email_load_digest'),

      // Messaging Permissions
      canCreateMessagingBroadcast: userPermissionsSet.has('create_messaging_broadcast'),
      canCreateMessagingChannel: userPermissionsSet.has('create_messaging_channel'),
      canModerateMessagingMessage: userPermissionsSet.has('moderate_messaging_message'),

      // CSV Import Permissions
      canCreateImportedOrder: userPermissionsSet.has('create_imported_order'),
      canCreateImportedProject: userPermissionsSet.has('create_imported_project'),
      canCreateImportedTicket: userPermissionsSet.has('create_imported_ticket'),

      // Site Permissions
      canEditSite: userPermissionsSet.has('edit_site'),
      canDeleteSite: userPermissionsSet.has('delete_site'),
      canCreateSite: userPermissionsSet.has('create_site'),

      // Account Permissions
      canEditAccount: userPermissionsSet.has('edit_account'),
      canDeleteAccount: userPermissionsSet.has('delete_account'),
      canCreateAccount: userPermissionsSet.has('create_account'),

      //Rates Permissions
      canEditRate: userPermissionsSet.has('edit_rate'),
      canDeleteRate: userPermissionsSet.has('delete_rate'),
      canCreateRate: userPermissionsSet.has('create_rate'),
      // hide_rates on the user suppresses the role-granted view_rate server-side
      // (roleable.rb#hide_rates_suppressed_permission_names), so its absence here
      // means rates are hidden for this user. UI gate only — not BE enforcement.
      canViewRate: userPermissionsSet.has('view_rate'),

      // Material Rates Permissions
      canEditMaterialRate: userPermissionsSet.has('edit_material_rate'),
      canDeleteMaterialRate: userPermissionsSet.has('delete_material_rate'),
      canCreateMaterialRate: userPermissionsSet.has('create_material_rate'),

      //Company Permissions
      canEditCompany: userPermissionsSet.has('edit_company'),
      canDeleteCompany: userPermissionsSet.has('delete_company'),
      canCreateCompany: userPermissionsSet.has('create_company'),
      canConnectCompany: userPermissionsSet.has('connect_companies'),

      // Routing Settings Permissions
      canEditRoutingSettings: userPermissionsSet.has('edit_routing_settings'),

      // Job permissions
      canAssignJob: userPermissionsSet.has('assign_job'),
      canCancelJob: userPermissionsSet.has('cancel_job'),
      canCreateJob: userPermissionsSet.has('create_job'),
      canDeleteJob: userPermissionsSet.has('delete_job'),
      canEditJob: userPermissionsSet.has('edit_job'),
      canApproveJob: userPermissionsSet.has('approve_job'),

      // Loads permissions
      canApproveLoad: userPermissionsSet.has('approve_load'),
      canCreateLoad: userPermissionsSet.has('create_load'),
      canDeleteLoad: userPermissionsSet.has('delete_load'),
      canEditLoad: userPermissionsSet.has('edit_load'),

      // Equipment Permissions
      canEditEquipment: userPermissionsSet.has('edit_equipment'),
      canDeleteEquipment: userPermissionsSet.has('delete_equipment'),
      canCreateEquipment: userPermissionsSet.has('create_equipment'),

      // Tickets permissions
      canCreateTicket: userPermissionsSet.has('create_ticket'),
      canEditTicket: userPermissionsSet.has('edit_ticket'),
      canDeleteTicket: userPermissionsSet.has('delete_ticket'),
      canEditTicketPotentialDuplicate: userPermissionsSet.has(
        'edit_ticket_potential_duplicate',
      ),

      // Department Permissions
      canEditDepartment: userPermissionsSet.has('edit_department'),
      canDeleteDepartment: userPermissionsSet.has('delete_department'),
      canCreateDepartment: userPermissionsSet.has('create_department'),

      // M2M Client Permissions
      canViewM2MClient: userPermissionsSet.has('read_m2m_client'),
      canCreateM2MClient: userPermissionsSet.has('create_m2m_client'),
      canEditM2MClient: userPermissionsSet.has('edit_m2m_client'),
      canRevokeM2MClient: userPermissionsSet.has('delete_m2m_client'),

      // Match Key Permissions
      canViewMatchKey: userPermissionsSet.has('read_match_key'),
      canCreateMatchKey: userPermissionsSet.has('create_match_key'),
      canDeleteMatchKey: userPermissionsSet.has('delete_match_key'),

      // Equipment Type Permissions
      canEditEquipmentType: userPermissionsSet.has('edit_equipment_type'),
      canDeleteEquipmentType: userPermissionsSet.has('delete_equipment_type'),
      canCreateEquipmentType: userPermissionsSet.has('create_equipment_type'),

      // Material Type Permissions
      canEditMaterialType: userPermissionsSet.has('edit_material_type'),
      canDeleteMaterialType: userPermissionsSet.has('delete_material_type'),
      canCreateMaterialType: userPermissionsSet.has('create_material_type'),

      // Add On Permissions
      canDeleteAddOn: userPermissionsSet.has('delete_add_on'),

      // Service Class Permissions
      canEditServiceClass: userPermissionsSet.has('edit_service_class'),
      canDeleteServiceClass: userPermissionsSet.has('delete_service_class'),
      canCreateServiceClass: userPermissionsSet.has('create_service_class'),

      // Telematics Permissions
      canEditTelematicsRemoteVehicle: userPermissionsSet.has(
        'edit_telematics_remote_vehicle',
      ),
      canManageTelematicsIntegration: userPermissionsSet.has(
        'manage_telematics_integration',
      ),

      // Accounting Integration
      canManageAccountingIntegration: userPermissionsSet.has(
        'manage_accounting_integration',
      ),

      // HaulWare Integration
      canManageHaulWareIntegration: userPermissionsSet.has(
        'manage_haul_ware_integration',
      ),
      canDeleteHaulWareIntegration: userPermissionsSet.has(
        'delete_haul_ware_integration',
      ),

      // Command Alkon Integration
      canManageCommandAlkonIntegration: userPermissionsSet.has(
        'manage_command_alkon_integration',
      ),
      canDeleteCommandAlkonIntegration: userPermissionsSet.has(
        'delete_command_alkon_integration',
      ),

      // VetaCenter Integration
      canManageVetaCenterIntegration: userPermissionsSet.has(
        'manage_veta_center_integration',
      ),
      canDeleteVetaCenterIntegration: userPermissionsSet.has(
        'delete_veta_center_integration',
      ),

      // Fast-Weigh Integration
      canManageFastWeighIntegration: userPermissionsSet.has(
        'manage_fast_weigh_integration',
      ),
      canDeleteFastWeighIntegration: userPermissionsSet.has(
        'delete_fast_weigh_integration',
      ),

      // Drivers Permissions
      canTextDrivers: userPermissionsSet.has('send_drivers_sms'),

      // Driver Days Permissions
      canCreateDriverDays: userPermissionsSet.has('create_driver_day'),
      canEditDriverDays: userPermissionsSet.has('edit_driver_day'),
      canDeleteDriverDays: userPermissionsSet.has('delete_driver_day'),
      canApproveDriverDays: userPermissionsSet.has('approve_driver_day'),

      // Shift Permissions
      canApproveShift: userPermissionsSet.has('approve_shift'),

      canEditSettlement: userPermissionsSet.has('edit_settlement'),

      // Label Permissions — BE maps manage_labels to a single "manage_labels" permission
      canCreateLabel: userPermissionsSet.has('manage_labels'),
      canEditLabel: userPermissionsSet.has('manage_labels'),
      canDeleteLabel: userPermissionsSet.has('manage_labels'),

      canCreateTreadAgentSkill: userPermissionsSet.has('create_tread_agent_skill'),
      canEditTreadAgentSkill: userPermissionsSet.has('edit_tread_agent_skill'),
      canDeleteTreadAgentSkill: userPermissionsSet.has('delete_tread_agent_skill'),

      canImpersonate: userPermissionsSet.has('impersonate'),

      // Approval Category Permissions
      canBillingApprove: userPermissionsSet.has('approval_category_billing'),
      canFieldApprove: userPermissionsSet.has('approval_category_field'),
    } as const;
  }
  setAccess(access: Access[]) {
    runInAction(() => {
      this.userAccess = [...access];
    });
  }
  getAccess() {
    // make userAccess a Set to make it faster and easier to check if a user has a permission
    const userAccessSet = new Set(this.userAccess.map((access) => access.name));

    return {
      manageCompanies: userAccessSet.has('manage_companies'),
      manageUser: userAccessSet.has('manage_users'),
      manageAccounts: userAccessSet.has('manage_accounts'),
      manageEquipment: userAccessSet.has('manage_equipment'),
      manageMaterials: userAccessSet.has('manage_materials'),
      manageSites: userAccessSet.has('manage_sites'),
      manageRates: userAccessSet.has('manage_rates'),
      manageDepartments: userAccessSet.has('manage_departments'),
      manageLabels: userAccessSet.has('manage_labels'),
      manageServiceClasses: userAccessSet.has('manage_service_classes'),
      manageTelematics: userAccessSet.has('manage_telematics'),
      manageDispatch: userAccessSet.has('manage_dispatch'),
      manageCompliance: userAccessSet.has('manage_compliance'),
      manageOvertimeRules: userAccessSet.has('manage_overtime_rules'),
      managePrevailingWageBenefits: userAccessSet.has('manage_prevailing_wage_benefits'),
      manageM2MClients: userAccessSet.has('manage_m2m_clients'),
      manageBilling: userAccessSet.has('manage_billing'),

      salesTab: userAccessSet.has('sales_tab'),
      dispatchTab: userAccessSet.has('dispatch_tab'),
      approvalsTab: userAccessSet.has('approvals_tab'),
      liveMapTab: userAccessSet.has('live_map_tab'),
      reportsTab: userAccessSet.has('reports_tab'),
      settingsTab: userAccessSet.has('settings_tab'),
      assignedTab: userAccessSet.has('assigned_tab'),
      assignedTabDriver: userAccessSet.has('assigned_tab_driver'),
      assignedTabForeman: userAccessSet.has('assigned_tab_foreman'),
      assignedTabSalesperson: userAccessSet.has('assigned_tab_salesperson'),
    };
  }
}

export default UserStore;
